import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { analyticsApi, disputesApi, exportApi, storesApi } from '../api/client';
import clsx from 'clsx';
import {
  CurrencyDollarIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowRightIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  TableCellsIcon,
  DocumentChartBarIcon,
  PlusCircleIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import SetupWizard from '../components/SetupWizard';

interface Summary {
  total_orders: number;
  total_refunds: number;
  at_risk: number;
  recovered: number;
  win_rate: number;
  pending_disputes: number;
}

interface TrendData {
  date: string;
  refund_amount: number;
  recovered_amount: number;
}

interface BucketData {
  bucket: string;
  count: number;
  refund_amount: number;
  win_rate: number;
}

const BUCKET_COLORS: Record<string, string> = {
  B1: '#3b82f6',
  B2: '#8b5cf6',
  B3: '#f59e0b',
  B4: '#ef4444',
};

const BUCKET_NAMES: Record<string, string> = {
  B1: 'High Item Missing',
  B2: 'High Value Refund',
  B3: 'Full Order Refund',
  B4: 'Repeat Offender',
};

const BUCKET_DESCRIPTIONS: Record<string, string> = {
  B1: 'More than 50% of order items were marked as missing - unusually high for a single order',
  B2: 'Refund amount exceeds 50% of order value ($25+) - potential abuse of partial refunds',
  B3: 'Full order refund ($20+) claimed - customer received nothing but payment was reversed',
  B4: 'Customer has 3+ refund claims in 30 days - pattern of repeated refund requests',
};

type TimePeriod = '7' | '30' | '90';

const TIME_PERIOD_LABELS: Record<TimePeriod, string> = {
  '7': 'Last 7 days',
  '30': 'Last 30 days',
  '90': 'Last 90 days',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [trend, setTrend] = useState<TrendData[]>([]);
  const [buckets, setBuckets] = useState<BucketData[]>([]);
  const [recentDisputes, setRecentDisputes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [storeCount, setStoreCount] = useState<number | null>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30');
  const exportRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setExportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (type: 'summary' | 'disputes' | 'orders' | 'report') => {
    setExporting(type);
    try {
      let response;
      let mimeType = 'text/csv';
      let extension = 'csv';
      let filename: string = type;

      if (type === 'report') {
        response = await exportApi.report('xlsx');
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        extension = 'xlsx';
        filename = 'recoverrefunds-report';
      } else {
        response = await exportApi[type]();
      }

      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}-${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const days = parseInt(timePeriod, 10);
        const [summaryRes, trendRes, bucketsRes, disputesRes, storesRes] = await Promise.all([
          analyticsApi.summary({ days }),
          analyticsApi.trend({ days }),
          analyticsApi.buckets({ days }),
          disputesApi.list({ limit: 5 }),
          storesApi.list(),
        ]);

        setSummary(summaryRes.data);
        setTrend(trendRes.data.data || []);
        setBuckets(bucketsRes.data.data || []);
        setRecentDisputes(disputesRes.data.data || []);
        setStoreCount((storesRes.data.data || []).length);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [timePeriod]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  return (
    <div className="space-y-6">
      {/* Setup Wizard Modal */}
      {showSetupWizard && (
        <SetupWizard
          onComplete={() => {
            setShowSetupWizard(false);
            navigate('/disputes');
          }}
          onClose={() => setShowSetupWizard(false)}
        />
      )}

      {/* Onboarding Prompt for new users */}
      {storeCount === 0 && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <RocketLaunchIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Get Started in 5 Minutes</h2>
                <p className="text-blue-100 mt-1">
                  Connect your Uber Eats, DoorDash, or Grubhub account to start recovering refunds automatically.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowSetupWizard(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-lg flex-shrink-0"
            >
              <PlusCircleIcon className="w-5 h-5" />
              Connect Platform
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Monitor your refund recovery performance</p>
        </div>
        <div className="flex items-center gap-3">
          {storeCount !== null && storeCount > 0 && (
            <button
              onClick={() => setShowSetupWizard(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              <PlusCircleIcon className="w-4 h-4" />
              Add Platform
            </button>
          )}
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px]"
          >
            <option value="7">{TIME_PERIOD_LABELS['7']}</option>
            <option value="30">{TIME_PERIOD_LABELS['30']}</option>
            <option value="90">{TIME_PERIOD_LABELS['90']}</option>
          </select>
          <div className="relative" ref={exportRef}>
            <button
              onClick={() => setExportOpen(!exportOpen)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export
            </button>
            {exportOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                <button
                  onClick={() => handleExport('summary')}
                  disabled={exporting !== null}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <DocumentChartBarIcon className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium">Summary Report</p>
                    <p className="text-xs text-slate-500">Overview metrics</p>
                  </div>
                  {exporting === 'summary' && (
                    <div className="ml-auto w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  )}
                </button>
                <button
                  onClick={() => handleExport('disputes')}
                  disabled={exporting !== null}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <DocumentTextIcon className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium">Disputes CSV</p>
                    <p className="text-xs text-slate-500">All dispute records</p>
                  </div>
                  {exporting === 'disputes' && (
                    <div className="ml-auto w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  )}
                </button>
                <button
                  onClick={() => handleExport('orders')}
                  disabled={exporting !== null}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <TableCellsIcon className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium">Orders CSV</p>
                    <p className="text-xs text-slate-500">All order records</p>
                  </div>
                  {exporting === 'orders' && (
                    <div className="ml-auto w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  )}
                </button>
                <div className="border-t border-slate-100 my-1" />
                <button
                  onClick={() => handleExport('report')}
                  disabled={exporting !== null}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-blue-600">Full Report (XLSX)</p>
                    <p className="text-xs text-slate-500">All data with multiple sheets</p>
                  </div>
                  {exporting === 'report' && (
                    <div className="ml-auto w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <MetricCard
          title="At Risk"
          value={formatCurrency(summary?.at_risk || 0)}
          change={12}
          changeType="negative"
          icon={CurrencyDollarIcon}
          iconBg="bg-gradient-to-br from-red-500 to-rose-600"
          description="Potential fraud detected"
        />
        <MetricCard
          title="Recovered"
          value={formatCurrency(summary?.recovered || 0)}
          change={8}
          changeType="positive"
          icon={CheckCircleIcon}
          iconBg="bg-gradient-to-br from-emerald-500 to-green-600"
          description="Successfully disputed"
        />
        <MetricCard
          title="Win Rate"
          value={`${summary?.win_rate || 0}%`}
          change={3}
          changeType="positive"
          icon={ChartBarIcon}
          iconBg="bg-gradient-to-br from-blue-500 to-indigo-600"
          description="Dispute success rate"
        />
        <MetricCard
          title="Ready to Review"
          value={String(summary?.pending_disputes || 0)}
          icon={ClockIcon}
          iconBg="bg-gradient-to-br from-amber-500 to-orange-600"
          description="Disputes needing your action"
          action={{ label: 'Review & Submit', href: '/disputes?status=pending_review' }}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Recovery Trend</h2>
            <p className="text-sm text-slate-500">Refunds vs recovered amount over time</p>
          </div>
          <div className="p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    stroke="#94a3b8"
                    fontSize={12}
                  />
                  <YAxis tickFormatter={(value) => `$${value}`} stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    labelFormatter={(label) => new Date(label).toLocaleDateString()}
                    contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="refund_amount"
                    stroke="#ef4444"
                    name="Refunds"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="recovered_amount"
                    stroke="#10b981"
                    name="Recovered"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-slate-600">Refunds</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-slate-600">Recovered</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bucket Distribution */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Fraud Distribution</h2>
            <p className="text-sm text-slate-500">By classification bucket</p>
          </div>
          <div className="p-6">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={buckets}
                    dataKey="count"
                    nameKey="bucket"
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                  >
                    {buckets.map((entry) => (
                      <Cell key={entry.bucket} fill={BUCKET_COLORS[entry.bucket] || '#6b7280'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3 mt-4">
              {buckets.map((b) => (
                <div key={b.bucket} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: BUCKET_COLORS[b.bucket] }}
                    />
                    <span className="text-sm text-slate-700">{BUCKET_NAMES[b.bucket] || b.bucket}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-900">{b.count}</span>
                    <span className="text-xs text-slate-500">{b.win_rate}% win</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Disputes */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Recent Disputes</h2>
            <p className="text-sm text-slate-500">Latest dispute activity</p>
          </div>
          <Link
            to="/disputes"
            className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            View all
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Store</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Bucket</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recentDisputes.map((dispute) => (
                <tr key={dispute.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm text-slate-900">{dispute.id.slice(0, 8)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900">{dispute.store_name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-slate-900">
                      {formatCurrency(dispute.order?.refund_amount || 0)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <BucketBadge bucket={dispute.bucket} />
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={dispute.status} />
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-500">
                      {new Date(dispute.created_at).toLocaleDateString()}
                    </span>
                  </td>
                </tr>
              ))}
              {recentDisputes.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <ClockIcon className="w-6 h-6 text-slate-400" />
                      </div>
                      <p className="text-slate-500">No disputes found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  change?: number;
  changeType?: 'positive' | 'negative';
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  description?: string;
  action?: { label: string; href: string };
}

function MetricCard({ title, value, change, changeType, icon: Icon, iconBg, description, action }: MetricCardProps) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={clsx('flex items-center justify-center w-12 h-12 rounded-xl', iconBg)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {change !== undefined && (
          <div
            className={clsx(
              'flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium',
              changeType === 'positive'
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-red-50 text-red-700'
            )}
          >
            {changeType === 'positive' ? (
              <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
            ) : (
              <ArrowTrendingDownIcon className="w-3.5 h-3.5" />
            )}
            {change}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
        {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
        {action && (
          <Link
            to={action.href}
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 mt-3"
          >
            {action.label}
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string }> = {
    draft: { bg: 'bg-slate-100', text: 'text-slate-700' },
    pending_review: { bg: 'bg-amber-100', text: 'text-amber-700' },
    approved: { bg: 'bg-blue-100', text: 'text-blue-700' },
    submitted: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
    won: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    lost: { bg: 'bg-red-100', text: 'text-red-700' },
    expired: { bg: 'bg-slate-100', text: 'text-slate-700' },
  };

  const style = styles[status] || styles.draft;

  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize', style.bg, style.text)}>
      {status.replace('_', ' ')}
    </span>
  );
}

function BucketBadge({ bucket }: { bucket: string }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-flex">
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-help"
        style={{
          backgroundColor: `${BUCKET_COLORS[bucket]}15`,
          color: BUCKET_COLORS[bucket],
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {bucket}
      </span>
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-lg">
          <p className="font-semibold mb-1">{BUCKET_NAMES[bucket]}</p>
          <p className="text-slate-300">{BUCKET_DESCRIPTIONS[bucket]}</p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
        </div>
      )}
    </div>
  );
}
